import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Smartphone, Mail, MapPin, Send, Clock } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Champs requis"); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(c.success); setForm({ name: "", email: "", message: "" });
    } catch (err) { toast.error("Erreur"); } finally { setSending(false); }
  };

  const info = [
    { icon: Phone, label: c.office, value: "067/87.05.53", href: "tel:+3267870553" },
    { icon: Smartphone, label: c.gsm, value: "0472/19.52.45", href: "tel:+32472195245" },
    { icon: Mail, label: c.email, value: "i.lease.be@gmail.com", href: "mailto:i.lease.be@gmail.com" },
    { icon: MapPin, label: c.address, value: "Rue Charles Jaumotte 29-31, 1300 Limal", href: "#" },
  ];

  return (
    <section id="contact" className="relative py-24 sm:py-32 max-w-6xl mx-auto px-5 sm:px-8" data-testid="contact">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="flex flex-col items-center text-center mb-16" data-testid="contact-logo">
        <img src="/ilease-emblem.png" alt="Logo I-LEASE SRL" className="h-24 w-auto object-contain drop-shadow-[0_0_30px_rgba(0,102,255,0.5)] animate-float" />
        <span className="mt-4 font-display font-extrabold text-3xl tracking-tight">I-LEASE<span className="text-accent ml-1.5">SRL</span></span>
        <span className="mt-2 text-sm text-muted-foreground uppercase tracking-[0.25em]">Leasing · Renting · LLD · Depuis 2015</span>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl sm:text-5xl font-bold">{c.title}</h2>
          <p className="mt-4 text-muted-foreground text-lg">{c.subtitle}</p>
          <div className="mt-8 space-y-3">
            {info.map(({ icon: Icon, label, value, href }, i) => (
              <motion.a key={i} href={href} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="flex items-center gap-4 rounded-xl border border-white/10 bg-card p-4 hover:border-accent/40 transition-colors group" data-testid={`contact-info-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-accent/15 flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Icon className="w-5 h-5 text-accent group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
                  <p className="text-sm font-medium">{value}</p>
                </div>
              </motion.a>
            ))}
          </div>
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4 text-accent" strokeWidth={1.5} /> Lundi - Vendredi · 9h00 - 18h00
          </div>
          <p className="mt-4 text-sm text-accent/90">{c.notSure}</p>
        </div>

        <form onSubmit={submit} className="glow-border glass rounded-2xl p-6 sm:p-8 space-y-4 h-fit" data-testid="contact-form">
          <Input placeholder={c.name} value={form.name} onChange={set("name")} data-testid="contact-name" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
          <Input type="email" placeholder={c.email} value={form.email} onChange={set("email")} data-testid="contact-email" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
          <Textarea placeholder={c.msg} rows={6} value={form.message} onChange={set("message")} data-testid="contact-message" className="bg-white/5 border-white/10 focus-visible:ring-accent" />
          <Button type="submit" disabled={sending} data-testid="contact-submit"
            className="w-full rounded-full bg-accent hover:bg-accent/90 text-white h-12 font-medium">
            <Send className="w-4 h-4 mr-2" strokeWidth={1.5} /> {sending ? c.sending : c.send}
          </Button>
        </form>
      </div>
    </section>
  );
};
